import { Skeleton, Typography } from '@mui/joy';
import {
  FetchBaseQueryError,
  TypedUseQueryHookResult,
} from '@reduxjs/toolkit/query/react';
import React, { ComponentProps } from 'react';
import { z } from 'zod';

import { standardErrorSchema } from './rest-api-error';

import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import { useTypedSelector } from '~/utils/store';

const queryErrorSchema = z.object({
  status: z.number(),
  data: standardErrorSchema,
});

const DefaultLoadingElement = () => {
  return (
    <div className='relative'>
      <Skeleton level='body-sm' variant='text' width='inherit' />
      <Skeleton level='body-sm' variant='text' width='inherit' />
      <Skeleton level='body-sm' variant='text' width='inherit' />
    </div>
  );
};

type ResolveStrategy = 'default' | 'stale-while-revalidate';
const STRATEGY_CONDITIONS: Record<
  ResolveStrategy,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <Query extends TypedUseQueryHookResult<unknown, unknown, any>>(
    q: Query,
  ) => boolean
> = {
  default: (q) => !q.isFetching,
  'stale-while-revalidate': () => true,
};
export default function QueryResolver<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends TypedUseQueryHookResult<unknown, unknown, any>,
  TData = T['data'],
>({
  children,
  strategy = 'default',
  query,
  onRefetch: onRefetchHandler = query.refetch,
  loadingElement = <DefaultLoadingElement />,
  errorElement = null,
  uninitializedElement = null,
}: Readonly<{
  query: T;
  strategy?: ResolveStrategy;
  children: ((d: NonNullable<TData>) => React.ReactNode) | React.ReactNode;
  onRefetch?: () => void;
  loadingElement?: React.ReactNode;
  errorElement?:
    | ((error: FetchBaseQueryError) => React.ReactNode)
    | React.ReactNode;
  uninitializedElement?: React.ReactNode;
}>) {
  const resolveCondition = STRATEGY_CONDITIONS[strategy](query);

  if (query.isUninitialized) {
    return <>{uninitializedElement}</>;
  }

  if (query.isError) {
    if (errorElement !== null) {
      return (
        <>
          {typeof errorElement === 'function'
            ? errorElement(query.error)
            : errorElement}
        </>
      );
    }

    const errorObject = queryErrorSchema.safeParse(query.error);
    if (errorObject.success) {
      const errorMessages = errorObject.data.data.errors.map(
        (serverError) => serverError.detail,
      );
      return (
        <div className='flex items-center justify-center px-4 py-16'>
          <div>
            <div>
              <Typography level='title-lg'>Error</Typography>
              <Text className='whitespace-pre-line'>
                {errorMessages.join('\r\n')}
              </Text>
            </div>
            <div className='mt-2'>
              <Button
                color='neutral'
                variant='soft'
                size='md'
                onClick={onRefetchHandler}
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='flex items-center justify-center px-4 py-16'>
        <div>
          <div>
            <Typography level='title-lg'>Error</Typography>
            <Typography>Failed to load data. Try again.</Typography>
          </div>
          <div className='mt-2'>
            <Button
              color='neutral'
              variant='soft'
              size='md'
              onClick={onRefetchHandler}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /**
   *
   * In the future we can consolidate this behavior to a prop to
   * choose if we want to show the cached data while fetching a new one
   * or show the loading element when the query is fetching.
   */
  if (query.isSuccess && resolveCondition && query.data !== undefined) {
    return (
      <>
        {typeof children === 'function'
          ? children(query.data as NonNullable<TData>)
          : children}
      </>
    );
  }

  return <>{loadingElement}</>;
}

export function QueryRevalidating({
  children,
  endpointName,
}: Readonly<{
  children: React.ReactNode;
  endpointName?: string;
}>) {
  const isSomeQueryFetching = useTypedSelector((state) =>
    Object.values(state.api.queries)
      .filter((query) =>
        endpointName ? query?.endpointName === endpointName : true,
      )
      .some((query) => query?.status === 'pending'),
  );
  if (isSomeQueryFetching) {
    return <>{children}</>;
  }
  return null;
}

type RevalidationIndicatorProps = Pick<
  ComponentProps<typeof QueryRevalidating>,
  'endpointName'
>;

export function RevalidationIndicator(
  props: Readonly<RevalidationIndicatorProps>,
) {
  return (
    <QueryRevalidating endpointName={props.endpointName}>
      <div className='flex gap-1 items-center'>
        <svg
          className='animate-spin h-3 w-3 text-neutral-400'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          ></circle>
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          ></path>
        </svg>
        <span className='text-xs/6 font-semibold text-neutral-400'>
          Syncing
        </span>
      </div>
    </QueryRevalidating>
  );
}
